import axios from 'axios';
import { useCallback, useRef, useState } from 'react';
import { Setting, Subscription } from '../App';
import { STOREFRONT_API_KEY, STOREFRONT_API_URL } from '../constants';

export type CategoryProduct = {
  productId: string;
  title: string;
  onlineStorePreviewUrl?: string;
  images: string[];
  isSetItem?: boolean;
  variants: {
    variantId: string;
    requiresShipping: boolean;
    id?: string;
    price?: number;
    sku?: string;
    sellingPlanAllocations?: {
      sellingPlan: { id: string };
      priceAdjustments: { price: { amount: number } }[];
    }[];
  }[];
  tags?: string[];
  handle?: string;
  price?: number;
  sku?: string;
  quantity?: number;
};

type CategoryCache = {
  [categoryId: string]: CategoryProduct[];
};

type LoadingState = {
  [categoryId: string]: boolean;
};

export const useCategoryProducts = (setting: Setting, subscription: Subscription | undefined) => {
  const [categoryCache, setCategoryCache] = useState<CategoryCache>({});
  const [loadingState, setLoadingState] = useState<LoadingState>({});

  // refで状態を管理（非同期処理での競合を防ぐ）
  const categoryCacheRef = useRef<CategoryCache>({});
  const loadingPromiseRef = useRef<{ [categoryId: string]: Promise<CategoryProduct[]> | undefined }>({});

  // カテゴリ定義
  const categories = [
    { title: '新商品', anker_id: 'new' },
    { title: 'ご飯もの', anker_id: 'rice' },
    { title: '麺', anker_id: 'noodle' },
    { title: 'おかず', anker_id: 'side_dish' },
    { title: 'ベーカリー', anker_id: 'bakery' },
    { title: 'デザート', anker_id: 'dessert' },
    { title: 'その他', anker_id: 'other' },
  ];

  // Storefront APIでカテゴリ別に商品を取得
  const loadCategoryFromStorefront = async (categoryTitle: string): Promise<CategoryProduct[]> => {
    if (!subscription) return [];

    try {
      // タグでフィルタしてStorefront APIから取得
      // 「その他」はShopify検索エンジンでトークン化されずヒットしないため、
      // tag:allで全商品取得後にクライアント側でフィルタする
      const useClientFilter = categoryTitle === 'その他';
      const query = useClientFilter
        ? `tag:'all' AND NOT tag:'マイページAdditional_Order非表示'`
        : `tag:'${categoryTitle}' AND NOT tag:'マイページAdditional_Order非表示'`;

      const result = await axios.post(
        STOREFRONT_API_URL!,
        {
          query: `
            query {
              products(first: 100, query: "${query}") {
                nodes {
                  id
                  title
                  handle
                  tags
                  onlineStoreUrl
                  images(first: 5) {
                    nodes {
                      url
                    }
                  }
                  variants(first: 10) {
                    nodes {
                      id
                      price {
                        amount
                      }
                      sku
                      requiresShipping
                      sellingPlanAllocations(first: 5) {
                        nodes {
                          sellingPlan {
                            id
                          }
                          priceAdjustments {
                            price {
                              amount
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
        },
        {
          headers: {
            'X-Shopify-Storefront-Access-Token': STOREFRONT_API_KEY,
          },
        },
      );

      const products = result.data.data?.products?.nodes || [];

      // CategoryProduct型に変換
      const categoryProducts: CategoryProduct[] = products.map((product: any) => {
        const firstVariant = product.variants?.nodes?.[0];
        return {
          productId: product.id,
          title: product.title,
          handle: product.handle,
          tags: product.tags || [],
          onlineStorePreviewUrl: product.onlineStoreUrl || '',
          images: product.images?.nodes?.map((img: any) => img.url) || [],
          variants:
            product.variants?.nodes?.map((variant: any) => ({
              variantId: variant.id,
              id: variant.id?.replace('gid://shopify/ProductVariant/', ''),
              requiresShipping: variant.requiresShipping ?? true,
              price: variant.price?.amount ? Math.round(Number(variant.price.amount)) : 0,
              sku: variant.sku || '',
              sellingPlanAllocations:
                variant.sellingPlanAllocations?.nodes?.map((alloc: any) => ({
                  sellingPlan: { id: alloc.sellingPlan?.id },
                  priceAdjustments:
                    alloc.priceAdjustments?.map((adj: any) => ({
                      price: { amount: Number(adj.price?.amount || 0) },
                    })) || [],
                })) || [],
            })) || [],
          price: firstVariant?.price?.amount ? Math.round(Number(firstVariant.price.amount)) : 0,
          sku: firstVariant?.sku || '',
        };
      });

      // 「その他」の場合、クライアント側でタグフィルタ
      const tagFilteredProducts = useClientFilter
        ? categoryProducts.filter((product) => product.tags?.includes(categoryTitle))
        : categoryProducts;

      // フィルタリング（購読済み商品の除外は表示時に行う）
      const filteredProducts = tagFilteredProducts.filter((product) => {
        // 配送要件チェック
        const hasIncompatibleShipping = product.variants.every(
          (variant) =>
            (variant.requiresShipping && !subscription.deliveryCountryCode) ||
            (!variant.requiresShipping && !!subscription.deliveryCountryCode),
        );
        if (hasIncompatibleShipping) return false;

        return true;
      });

      return filteredProducts;
    } catch (e) {
      console.error('Failed to load category products:', e);
      return [];
    }
  };

  // カテゴリの商品を取得
  const getCategoryProducts = useCallback(
    async (categoryTitle: string, categoryId: string) => {
      // 既にキャッシュがあればそれを返す
      if (categoryCacheRef.current[categoryId]) {
        return categoryCacheRef.current[categoryId];
      }

      // 既に読み込み中なら、その Promise を返す
      if (loadingPromiseRef.current[categoryId]) {
        return loadingPromiseRef.current[categoryId];
      }

      // ローディング中に設定
      setLoadingState((prev) => ({ ...prev, [categoryId]: true }));

      // このカテゴリの商品をStorefront APIから取得
      const loadPromise = loadCategoryFromStorefront(categoryTitle);
      loadingPromiseRef.current[categoryId] = loadPromise;

      try {
        const categoryProducts = await loadPromise;

        // キャッシュに保存
        categoryCacheRef.current[categoryId] = categoryProducts;
        setCategoryCache((prev) => ({ ...prev, [categoryId]: categoryProducts }));

        return categoryProducts;
      } finally {
        setLoadingState((prev) => ({ ...prev, [categoryId]: false }));
        delete loadingPromiseRef.current[categoryId];
      }
    },
    [subscription],
  );

  // カテゴリがローディング中かどうか
  const isCategoryLoading = useCallback(
    (categoryId: string) => {
      return loadingState[categoryId] || false;
    },
    [loadingState],
  );

  // カテゴリの商品数を取得
  const getCategoryProductCount = useCallback(
    (categoryId: string) => {
      return categoryCache[categoryId]?.length;
    },
    [categoryCache],
  );

  return {
    categories,
    getCategoryProducts,
    isCategoryLoading,
    getCategoryProductCount,
    categoryCache,
  };
};
